import { matchPath } from 'react-router-dom';

/**
 * 페이지 상단·하단 배너 — 경로 → 배너 위치(2026-10-12). **어느 화면에 배너가 나오고 어느 화면에서 빼는지는 이 파일 한 곳에서만 정한다.**
 * 위치 이름은 서버 BannerLocation 과 같다(core banner/type/BannerLocation.kt). 목록 페이지는 옛 값(RECRUITMENT_TOP 등)이 곧 "목록 상단/하단".
 */

export type BannerPlacement = 'top' | 'bottom';

export type BannerPage =
    | 'HOME' | 'RECRUITMENT_LIST' | 'RECRUITMENT_DETAIL' | 'SEMINAR_LIST' | 'SEMINAR_DETAIL'
    | 'POST_LIST' | 'POST_DETAIL' | 'EXPERT' | 'NOTICE' | 'MYPAGE';

/** 페이지 → [상단 위치, 하단 위치] */
export const PAGE_LOCATIONS: Record<BannerPage, [string, string]> = {
    HOME: ['HOME_TOP', 'HOME_BOTTOM'],
    RECRUITMENT_LIST: ['RECRUITMENT_TOP', 'RECRUITMENT_BOTTOM'],
    RECRUITMENT_DETAIL: ['RECRUITMENT_DETAIL_TOP', 'RECRUITMENT_DETAIL_BOTTOM'],
    SEMINAR_LIST: ['SEMINAR_TOP', 'SEMINAR_BOTTOM'],
    SEMINAR_DETAIL: ['SEMINAR_DETAIL_TOP', 'SEMINAR_DETAIL_BOTTOM'],
    POST_LIST: ['POST_TOP', 'POST_BOTTOM'],
    POST_DETAIL: ['POST_DETAIL_TOP', 'POST_DETAIL_BOTTOM'],
    EXPERT: ['EXPERT_TOP', 'EXPERT_BOTTOM'],
    NOTICE: ['NOTICE_TOP', 'NOTICE_BOTTOM'],
    MYPAGE: ['MYPAGE_TOP', 'MYPAGE_BOTTOM'],
};

export const ALL_PAGES_LOCATIONS: [string, string] = ['ALL_TOP', 'ALL_BOTTOM'];

/**
 * "모든 페이지" 배너도 내지 않는 화면(사용자 확정 2026-10-12): 결제, 입력 폼(글쓰기·공고 등록·수정 등), 로그인·가입·비밀번호 찾기, AI 대화.
 * 아래 입력줄이 붙는 대화 화면(지원 건 대화방·문의 대화)도 입력 화면이라 함께 뺀다.
 * 위에서부터 맞춰 본다 — 상세 경로(`/posts/:postId`)보다 먼저 와야 하는 `/posts/new` 같은 경로가 여기 있다.
 */
export const BANNER_EXCLUDED_PATHS: readonly string[] = [
    // 결제
    '/seminars/:id/payment/*',
    '/points/charge',
    '/products/:category',
    // 입력 폼
    '/recruitments/new',
    '/recruitments/:id/survey',
    '/posts/new',
    '/posts/:postId/edit',
    '/expert-lounge/new',
    '/expert-lounge/:id/edit',
    '/seminars/new',
    '/seminars/:id/edit',
    '/seminar/instructor/apply',
    '/seminar/instructor/edit',
    '/profile/clinic/edit',
    '/profile/user/edit',
    '/profile/license-verification',
    '/resume',
    '/resume/edit',
    '/inquiry',
    '/salary-surveys/response',
    // 로그인·가입·비밀번호 찾기
    '/login',
    '/login/callback/*',
    '/signup',
    '/signup/success',
    '/password-reset',
    // AI 대화
    '/ai',
    '/ai/*',
    // 입력줄이 붙는 대화 화면
    '/applications/:applicationId/thread',
    '/inquiries/:inquiryId',
];

/**
 * 경로 → 페이지. `inPage` 인 화면은 배너 자리를 화면 안(목록 컨테이너의 필터 아래·목록 뒤, 홈 섹션 줄)에 직접 두고,
 * 앱 본문의 공용 자리(App.tsx)는 그 화면에서 비킨다. 나머지 화면은 공용 자리(본문 위·아래)를 쓴다.
 */
const PAGE_ROUTES: readonly { path: string; page: BannerPage | null; inPage?: boolean }[] = [
    { path: '/', page: 'HOME', inPage: true },
    { path: '/home-v2', page: 'HOME', inPage: true },
    { path: '/recruitments', page: 'HOME', inPage: true },
    { path: '/recruitments/region', page: 'RECRUITMENT_LIST', inPage: true },
    { path: '/recruitments/welfare', page: 'RECRUITMENT_LIST' },
    { path: '/recruitments/:id', page: 'RECRUITMENT_DETAIL' },
    { path: '/seminars', page: 'SEMINAR_LIST', inPage: true },
    // 상세 경로(`:id`)에 걸리는 다른 화면 — 페이지 위치 없음(모든 페이지 배너만)
    { path: '/seminars/history', page: null },
    { path: '/posts/my', page: null },
    { path: '/seminars/:id', page: 'SEMINAR_DETAIL' },
    { path: '/posts', page: 'POST_LIST', inPage: true },
    { path: '/posts/:postId', page: 'POST_DETAIL' },
    { path: '/posts/:postId/comments/:commentId', page: 'POST_DETAIL' },
    { path: '/expert-lounge', page: 'EXPERT', inPage: true },
    { path: '/expert-lounge/:id', page: 'EXPERT' },
    { path: '/expert-lounge/:id/comments/:commentId', page: 'EXPERT' },
    { path: '/notices', page: 'NOTICE', inPage: true },
    { path: '/notices/:noticeId', page: 'NOTICE' },
    { path: '/profile', page: 'MYPAGE' },
];

export interface PageBannerTarget {
    /** 이 화면에서 빼는가(모든 페이지 배너 포함) */
    excluded: boolean;
    /** 페이지 위치가 없는 화면(검색·약관 등)은 null — "모든 페이지" 배너만 나온다 */
    page: BannerPage | null;
    /** 화면이 배너 자리를 직접 두는가 */
    inPage: boolean;
    /** 한 번에 물을 위치 — [페이지 상단, 페이지 하단, 모든 상단, 모든 하단] (빠지는 화면은 빈 배열) */
    locations: string[];
}

export function resolvePageBanners(pathname: string): PageBannerTarget {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (BANNER_EXCLUDED_PATHS.some(pattern => matchPath({ path: pattern, end: true }, path))) {
        return { excluded: true, page: null, inPage: false, locations: [] };
    }
    const hit = PAGE_ROUTES.find(route => matchPath({ path: route.path, end: true }, path));
    const page = hit?.page ?? null;
    return {
        excluded: false,
        page,
        inPage: !!hit?.inPage,
        locations: [...(page ? PAGE_LOCATIONS[page] : []), ...ALL_PAGES_LOCATIONS],
    };
}
